import React, { useState } from 'react';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, MapPin, Clock, DollarSign, Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const OpenRoles = () => {
  const [expandedJob, setExpandedJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    coverLetter: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const jobListings = [
    {
      id: 'sdr-appointment-setter',
      title: 'B2B Appointment Setter / SDR',
      subtitle: 'LinkedIn, Facebook & Phone Outreach for an AI Training Platform',
      location: 'Remote',
      type: 'Full-time (40 hrs/week)',
      compensation: '$12-$14/hr + bonus per qualified demo held',
      description: `TRAAIN is an AI training and enablement platform. We help teams get better at the work that matters most: their calls, their conversations, and their skills. Our flagship product, Call Conductor, scores every call against a team's own standards. It then turns the moments that need work into same-day role-play practice built from the agent's real calls. See it. Fix it. Prove it.

We're a small, fast-moving team looking for an appointment setter who can own the top of our funnel. That means building the outreach, writing the messages, improving what works, and filling the calendar with qualified demos. You'll work directly with the founder and build a long-term relationship with our team.`,
      responsibilities: [
        'Book demos across two motions:',
        '• Call Conductor: agency owners and phone-based sales and customer service teams. Insurance agencies are a core audience today.',
        '• TRAAIN for Enterprise: L&D, HR, and enablement leaders at larger organizations, including education, healthcare, and financial services.',
        'Build and run LinkedIn outreach campaigns: research and identify ideal prospects, then reach senior decision-makers through manual, personalized outreach.',
        'Write the copy: connection requests, follow-ups, and conversation flows that sound like a person, not a template.',
        'Qualify and book: manage conversations, handle objections naturally, and book qualified demos directly into Calendly.',
        'Run Facebook outreach: engage in industry groups and follow up with inbound leads from our ads.',
        'Follow up by phone and email: call warm leads, re-engage cold ones, and confirm meetings to reduce no-shows.',
        'Own our monday CRM pipeline: log every lead, touch, stage change, and next step, keep sequences running, and move contacts through the pipeline. If it\'s not in monday CRM, it didn\'t happen.',
        'Optimize and report: test messaging, track what\'s working, and share weekly metrics. Those include outreach volume, connection acceptance rate, reply rate, meetings booked, and meetings held.'
      ],
      requirements: [
        'Proven B2B appointment-setting experience, ideally in SaaS or other high-value services',
        'A track record of booking meetings with founders, owners, directors, and C-level executives',
        'Hands-on experience with monday CRM (monday sales CRM), including pipelines, sequences, and activity logging. Strong experience in HubSpot or Salesforce is acceptable if you can ramp quickly.',
        'Experience building and managing LinkedIn outreach campaigns, not just executing someone else\'s',
        'Strong copywriting skills and excellent written English',
        'Comfort on the phone. You\'ll dial, not just message.',
        'Self-managing and reliable, with clear communication about your availability',
        'Bonus: experience selling into insurance agencies, L&D/HR buyers, or education; familiarity with Apollo or Sales Navigator'
      ],
      details: [
        'Remote, full-time (40 hrs/week), long-term, with room to grow',
        'Must overlap with US Mountain/Eastern business hours for calls',
        'Compensation: $12–$14/hr based on experience, plus a bonus for every qualified demo that is held',
        'We provide talking points, approved messaging, target lists, and product training'
      ],
      applicationRequirements: [
        'A summary of your appointment-setting experience and the buyers you\'ve booked with',
        'Your typical metrics: connection acceptance rate, reply rate, and booking rate, plus meetings booked per month',
        'A case study or example from a past campaign',
        'A LinkedIn connection request, under 300 characters, you\'d send to an agency owner about Call Conductor',
        'How you\'ve used monday CRM (or a similar CRM) to track an outreach pipeline',
        'Your weekly availability, time zone, and rate'
      ],
      applicationNote: 'Please start your cover letter with the word "Demo" so we know you read this.'
    },
    {
      id: 'executive-assistant',
      title: 'Executive Assistant / Virtual Assistant',
      subtitle: 'Remote (International) · Full-time Contractor',
      location: 'Remote (International)',
      type: 'Full-time (40 hrs/week)',
      compensation: '$5-$8/hr USD based on experience',
      description: `TRAAIN is an AI training and enablement platform. We help teams get better at the work that matters most: their calls, their conversations, and their skills. Our flagship product, Call Conductor, scores calls against a team's own standards and turns the moments that need work into same-day role-play practice.

We're a small, fast-moving team, and our General Manager needs a sharp, organized right hand. You'll keep calendars, inboxes, clients, events, and logistics running smoothly, so she can focus on growing the company. This is a hands-on role with real variety: some days are scheduling and email, and some days you're on the phone with clients or shipping materials to a conference.`,
      responsibilities: [
        'Calendar, email & phones:',
        '• Manage and coordinate calendars across the General Manager, team, clients, and partners',
        '• Book, confirm, and reschedule meetings in Calendly and Google Calendar',
        '• Triage the General Manager\'s inbox, draft replies for approval, and flag what needs her attention',
        '• Answer and make phone calls: client questions, meeting confirmations, vendor follow-ups, and rebooking no-shows',
        'Client coordination:',
        '• Schedule and coordinate client meetings, success sessions, and trainings, then send agendas, reminders, and follow-ups',
        '• Coordinate new-client onboarding: welcome emails, intake forms, roster collection, and recap emails',
        '• Help with account setup: adding users, teams, and assignments in our platform',
        '• Route support requests to the right place and log bugs clearly for our dev team',
        '• Send lifecycle emails: new-client welcomes, payment follow-ups, and renewal reminders',
        'Conferences, events & shipping:',
        '• Coordinate conferences and events end to end: registration, travel, booth materials, schedules, and attendee follow-up lists',
        '• Manage shipping logistics: booth kits, marketing materials, and equipment to and from events, with tracking and on-time delivery',
        '• Coordinate webinars: registration, reminders, hosting logistics, and replay emails',
        'Social media & marketing support:',
        '• Create and schedule LinkedIn and Facebook posts, and repurpose video clips and content',
        '• Monitor comments and messages, and flag leads to our sales team',
        'Sales & operations support:',
        '• Prepare short briefs on prospects before demos',
        '• Keep our monday CRM clean: log meeting outcomes, update stages, and make sure every contact has a next step',
        '• Compile weekly reports from Calendly, the CRM, and team activity',
        '• Keep documents, decks, and help resources up to date'
      ],
      requirements: [
        'Clear, fluent spoken and written English. You\'ll be on the phone with clients, so this is essential.',
        '2+ years as an executive assistant or VA, ideally supporting an executive at a startup or SaaS company',
        'Confident and professional on the phone',
        'Highly organized and detail-oriented. Nothing falls through the cracks.',
        'Experience coordinating events, travel, or shipping logistics',
        'Comfortable with Google Workspace, Calendly, and monday CRM (or similar), plus social media scheduling tools',
        'Proactive and self-managing. You see what needs doing and do it.',
        'Reliable, with clear communication about your availability',
        'Bonus: experience with SaaS customer success, Canva, or conference planning'
      ],
      details: [
        'Remote, full-time (40 hrs/week), long-term independent contractor role',
        'Must work US Mountain/Eastern business hours',
        'Compensation: $5–$8 USD/hr based on experience',
        'Full training on our platform and processes provided'
      ],
      applicationRequirements: [
        'A short summary of your EA/VA experience and who you\'ve supported',
        'A link to a 1–2 minute voice or video recording (Loom, Vocaroo, or similar) introducing yourself',
        'An example of an event, conference, or shipping project you coordinated, and how you handled it',
        'The tools you use daily (calendar, CRM, social scheduling, project management)',
        'Your weekly availability and time zone'
      ],
      applicationNote: 'Email careers@traain.com with the subject line "Executive Assistant – [Your Name]"',
      useEmailApplication: true
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setSubmitStatus({ type: 'error', message: 'Please upload a PDF, DOC, or DOCX file.' });
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSubmitStatus({ type: 'error', message: 'File size must be under 10MB.' });
        return;
      }
      setFormData(prev => ({ ...prev, resume: file }));
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e, jobTitle) => {
    e.preventDefault();
    
    if (!formData.resume) {
      setSubmitStatus({ type: 'error', message: 'Please upload your resume.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('coverLetter', formData.coverLetter);
      formDataToSend.append('position', jobTitle);
      formDataToSend.append('resume', formData.resume);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-application`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Your application has been submitted successfully! We\'ll be in touch soon.' });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          coverLetter: '',
          resume: null
        });
        setShowApplicationForm(false);
      } else {
        setSubmitStatus({ type: 'error', message: result.detail || 'Failed to submit application. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Failed to submit application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleJob = (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
      setShowApplicationForm(false);
    } else {
      setExpandedJob(jobId);
      setShowApplicationForm(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-gray-900">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            Open Positions
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Join our team and help build the future of AI-powered training and enablement.
          </p>
        </div>

        {/* Success/Error Message */}
        {submitStatus && !showApplicationForm && (
          <div className={`mb-8 p-4 rounded-lg flex items-center gap-3 ${
            submitStatus.type === 'success' ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
          }`}>
            {submitStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            )}
            <p className={submitStatus.type === 'success' ? 'text-green-300' : 'text-red-300'}>
              {submitStatus.message}
            </p>
          </div>
        )}

        {/* Job Listings */}
        <div className="space-y-6">
          {jobListings.map((job) => (
            <div 
              key={job.id}
              className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden"
            >
              {/* Job Header */}
              <button
                onClick={() => toggleJob(job.id)}
                className="w-full p-6 text-left flex items-start justify-between hover:bg-gray-800/70 transition-colors"
              >
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-1">
                    {job.title}
                  </h3>
                  <p className="text-cyan-400 text-sm mb-3">{job.subtitle}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" />
                      {job.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {job.type}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4" />
                      {job.compensation}
                    </span>
                  </div>
                </div>
                <div className="ml-4 text-gray-400">
                  {expandedJob === job.id ? (
                    <ChevronUp className="w-6 h-6" />
                  ) : (
                    <ChevronDown className="w-6 h-6" />
                  )}
                </div>
              </button>

              {/* Expanded Job Details */}
              {expandedJob === job.id && (
                <div className="px-6 pb-6 border-t border-gray-700">
                  <div className="pt-6 space-y-6">
                    {/* Description */}
                    <div>
                      <p className="text-gray-300 whitespace-pre-line leading-relaxed">
                        {job.description}
                      </p>
                    </div>

                    {/* What You'll Do */}
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-3">What You'll Do</h4>
                      <ul className="space-y-2">
                        {job.responsibilities.map((item, idx) => {
                          const isHeader = item.endsWith(':') && !item.startsWith('•');
                          const isBullet = item.startsWith('•');
                          return (
                            <li key={idx} className={`text-gray-300 ${isBullet ? 'ml-4' : ''} ${isHeader ? 'font-semibold text-white mt-4 first:mt-0' : ''}`}>
                              {!isBullet && !isHeader && '• '}{item}
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* What We're Looking For */}
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-3">What We're Looking For</h4>
                      <ul className="space-y-2">
                        {job.requirements.map((item, idx) => (
                          <li key={idx} className="text-gray-300">• {item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Details */}
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-3">Details</h4>
                      <ul className="space-y-2">
                        {job.details.map((item, idx) => (
                          <li key={idx} className="text-gray-300">• {item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* To Apply Section */}
                    <div className="bg-cyan-900/20 border border-cyan-800/50 rounded-lg p-5">
                      <h4 className="text-lg font-semibold text-white mb-3">
                        {job.useEmailApplication ? 'How to Apply:' : 'To Apply, Please Include:'}
                      </h4>
                      <ol className="space-y-2 mb-4">
                        {job.applicationRequirements.map((item, idx) => (
                          <li key={idx} className="text-gray-300">
                            <span className="text-cyan-400 font-medium">{idx + 1}.</span> {item}
                          </li>
                        ))}
                      </ol>
                      {job.applicationNote && (
                        <p className="text-cyan-300 text-sm italic">
                          {job.applicationNote}
                        </p>
                      )}
                    </div>

                    {/* Apply Button */}
                    {job.useEmailApplication ? (
                      <div className="pt-4">
                        <a
                          href={`mailto:careers@traain.com?subject=Executive%20Assistant%20%E2%80%93%20[Your%20Name]`}
                          className="inline-flex items-center justify-center bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 rounded-md font-medium transition-colors"
                          data-testid="apply-email-btn"
                        >
                          Apply via Email
                        </a>
                        <p className="text-gray-400 text-sm mt-3">
                          Opens your email client to send your application to careers@traain.com
                        </p>
                      </div>
                    ) : !showApplicationForm ? (
                      <div className="pt-4">
                        <Button
                          onClick={() => setShowApplicationForm(true)}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3"
                          data-testid="apply-now-btn"
                        >
                          Apply Now
                        </Button>
                      </div>
                    ) : (
                      /* Application Form */
                      <div className="pt-4 border-t border-gray-700">
                        <h4 className="text-xl font-semibold text-white mb-6">Submit Your Application</h4>
                        
                        {/* Form Status Messages */}
                        {submitStatus && (
                          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                            submitStatus.type === 'success' ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
                          }`}>
                            {submitStatus.type === 'success' ? (
                              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                            )}
                            <p className={submitStatus.type === 'success' ? 'text-green-300' : 'text-red-300'}>
                              {submitStatus.message}
                            </p>
                          </div>
                        )}

                        <form onSubmit={(e) => handleSubmit(e, job.title)} className="space-y-5">
                          <div className="grid md:grid-cols-2 gap-5">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                                First Name <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                placeholder="John"
                                data-testid="first-name-input"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                                Last Name <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                placeholder="Doe"
                                data-testid="last-name-input"
                              />
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-5">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                                Email <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                placeholder="john@example.com"
                                data-testid="email-input"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                                Phone <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                placeholder="+1 (555) 123-4567"
                                data-testid="phone-input"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Resume <span className="text-red-400">*</span>
                              <span className="text-gray-500 font-normal ml-2">(PDF, DOC, or DOCX - Max 10MB)</span>
                            </label>
                            <div className="relative">
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={handleFileChange}
                                className="hidden"
                                id="resume-upload"
                                data-testid="resume-upload"
                              />
                              <label
                                htmlFor="resume-upload"
                                className="flex items-center justify-center gap-3 w-full px-4 py-4 bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-cyan-500 hover:text-cyan-400 cursor-pointer transition-colors"
                              >
                                <Upload className="w-5 h-5" />
                                {formData.resume ? (
                                  <span className="text-cyan-400">{formData.resume.name}</span>
                                ) : (
                                  <span>Click to upload your resume</span>
                                )}
                              </label>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Cover Letter
                              <span className="text-gray-500 font-normal ml-2">(Include the items listed above)</span>
                            </label>
                            <textarea
                              name="coverLetter"
                              value={formData.coverLetter}
                              onChange={handleInputChange}
                              rows={8}
                              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 resize-none"
                              placeholder="Demo&#10;&#10;Start with 'Demo' and include your appointment-setting experience, metrics, a case study, a sample LinkedIn connection request, CRM experience, and your availability..."
                              data-testid="cover-letter-input"
                            />
                          </div>

                          <div className="flex gap-4 pt-2">
                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid="submit-application-btn"
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Submitting...
                                </>
                              ) : (
                                'Submit Application'
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowApplicationForm(false)}
                              className="border-gray-600 text-gray-300 hover:bg-gray-700"
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* General Application Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-400">
            Don't see a perfect fit? Send your resume to{' '}
            <a 
              href="mailto:Careers@traain.com" 
              className="text-cyan-400 hover:text-cyan-300 underline"
            >
              Careers@traain.com
            </a>
            {' '}— we're always looking for exceptional talent.
          </p>
        </div>
      </div>
    </section>
  );
};

export default OpenRoles;
